import React, { useState, useRef, useEffect } from 'react';
import EmojiPicker from 'emoji-picker-react';
import Button from '../common/Button';

const REPO_COMMANDS = [
  {
    command: '/analyze-repo',
    description: 'Analyze a code repository at the given path',
    example: '/analyze-repo /path/to/repository'
  },
  {
    command: '/analyze-file',
    description: 'Analyze a specific file in a repository',
    example: '/analyze-file /path/to/repository/file.js'
  }
];

const MessageInput = ({ onSendMessage, disabled, selectedRepo = null }) => {
  const [message, setMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showMathHelper, setShowMathHelper] = useState(false);
  const [showCommandHelper, setShowCommandHelper] = useState(false);
  const emojiPickerRef = useRef(null);
  const mathHelperRef = useRef(null);
  const commandHelperRef = useRef(null);
  const textareaRef = useRef(null);

  // Handle when a repository is selected from the code browser
  useEffect(() => {
    if (selectedRepo) {
      setMessage(prev => `/analyze-repo ${selectedRepo}`);
    }
  }, [selectedRepo]);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (message.trim() && !disabled) {
      onSendMessage(message);
      setMessage('');
    }
  };

  const handleKeyDown = (e) => {
    // Show command helper when typing '/'
    if (e.key === '/' && message === '') {
      setShowCommandHelper(true);
      return;
    }
    
    // Submit on Enter (but not with Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      handleSubmit(e);
      return;
    }
    
    // Tab completion for commands
    if (e.key === 'Tab' && message.startsWith('/') && !message.includes(' ')) {
      e.preventDefault();
      const matchingCommand = REPO_COMMANDS.find(cmd => 
        cmd.command.startsWith(message)
      );
      
      if (matchingCommand) {
        setMessage(`${matchingCommand.command} `);
      }
    }
  };

  const handleEmojiClick = (emojiData) => {
    setMessage((prev) => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const insertMathShortcut = (type) => {
    let insert = '';
    const textArea = textareaRef.current;
    const start = textArea.selectionStart;
    const end = textArea.selectionEnd;
    const selected = message.substring(start, end);
    
    switch(type) {
      case 'inline':
        insert = `$${selected}$`;
        break;
      case 'block':
        insert = `\n$$\n${selected}\n$$\n`;
        break;
      case 'fraction':
        insert = `\\frac{${selected || 'numerator'}}{denominator}`;
        break;
      case 'sqrt':
        insert = `\\sqrt{${selected || 'x'}}`;
        break;
      case 'sum':
        insert = `\\sum_{i=1}^{n} ${selected || 'x_i'}`;
        break;
      case 'integral':
        insert = `\\int_{a}^{b} ${selected || 'f(x)'} dx`;
        break;
      default:
        insert = selected;
    }
    
    const newMessage = message.substring(0, start) + insert + message.substring(end);
    setMessage(newMessage);
    setShowMathHelper(false);
    
    // Focus back on textarea and place cursor appropriately
    setTimeout(() => {
      textArea.focus();
      const newCursorPos = start + insert.length;
      textArea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };
  
  const insertCommand = (command) => {
    setMessage(`${command} `);
    setShowCommandHelper(false);
    
    // Focus the textarea and place cursor at the end
    setTimeout(() => {
      textareaRef.current.focus();
      const length = command.length + 1;
      textareaRef.current.setSelectionRange(length, length);
    }, 0);
  };

  // Close any popups when clicking outside
  const handleClickOutside = (e) => {
    if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
      setShowEmojiPicker(false);
    }
    if (mathHelperRef.current && !mathHelperRef.current.contains(e.target)) {
      setShowMathHelper(false);
    }
    if (commandHelperRef.current && !commandHelperRef.current.contains(e.target)) {
      setShowCommandHelper(false);
    }
  };

  // Add and remove event listener
  React.useEffect(() => {
    if (showEmojiPicker || showMathHelper || showCommandHelper) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker, showMathHelper, showCommandHelper]);

  return (
    <form
      className="border-t p-4 bg-white"
      onSubmit={handleSubmit}
    >
      <div className="relative">
        {/* Emoji Picker Popup */}
        {showEmojiPicker && (
          <div 
            className="absolute bottom-full mb-2 z-20" 
            ref={emojiPickerRef}
          >
            <EmojiPicker onEmojiClick={handleEmojiClick} />
          </div>
        )}
        
        {/* Math Helper Popup */}
        {showMathHelper && (
          <div 
            className="absolute bottom-full mb-2 bg-white border rounded-md shadow-lg p-2 z-20" 
            ref={mathHelperRef}
          >
            <div className="text-sm font-medium mb-2">Math Shortcuts</div>
            <div className="grid grid-cols-2 gap-2">
              <button 
                className="px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded"
                onClick={() => insertMathShortcut('inline')}
              >
                Inline Math ($...$)
              </button>
              <button 
                className="px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded"
                onClick={() => insertMathShortcut('block')}
              >
                Block Math ($$...$$)
              </button>
              <button 
                className="px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded"
                onClick={() => insertMathShortcut('fraction')}
              >
                Fraction (\\frac{}{})
              </button>
              <button 
                className="px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded"
                onClick={() => insertMathShortcut('sqrt')}
              >
                Square Root (\\sqrt{})
              </button>
              <button 
                className="px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded"
                onClick={() => insertMathShortcut('sum')}
              >
                Summation (\\sum)
              </button>
              <button 
                className="px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded"
                onClick={() => insertMathShortcut('integral')}
              >
                Integral (\\int)
              </button>
            </div>
          </div>
        )}
        
        {/* Command Helper Popup */}
        {showCommandHelper && (
          <div 
            className="absolute bottom-full mb-2 bg-white border rounded-md shadow-lg p-2 z-20 w-full max-w-lg" 
            ref={commandHelperRef}
          >
            <div className="text-sm font-medium mb-2">Repository Commands</div>
            <div className="space-y-2">
              {REPO_COMMANDS.map((cmd) => (
                <div 
                  key={cmd.command}
                  className="p-2 hover:bg-gray-100 rounded cursor-pointer"
                  onClick={() => insertCommand(cmd.command)}
                >
                  <div className="font-mono text-primary-600">{cmd.command}</div>
                  <div className="text-sm text-gray-600">{cmd.description}</div>
                  <div className="text-xs text-gray-500 italic">Example: {cmd.example}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="flex items-end">
          {/* Emoji Button */}
          <button
            type="button"
            className="p-2 text-gray-500 rounded-full hover:text-gray-700 focus:outline-none"
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            disabled={disabled}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-6 w-6" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
              />
            </svg>
          </button>
          
          {/* Math Button */}
          <button
            type="button"
            className="p-2 text-gray-500 rounded-full hover:text-gray-700 focus:outline-none"
            onClick={() => setShowMathHelper(!showMathHelper)}
            disabled={disabled}
            title="Math expressions"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-6 w-6" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7C5 4 4 5 4 7z M10 14l-2 2 M12 12l4-4 M9 8h1 M14 17h1" 
              />
            </svg>
          </button>
          
          {/* Commands Button */}
          <button
            type="button"
            className="p-2 text-gray-500 rounded-full hover:text-gray-700 focus:outline-none"
            onClick={() => setShowCommandHelper(!showCommandHelper)}
            disabled={disabled}
            title="Repository commands"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-6 w-6" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" 
              />
            </svg>
          </button>
          
          <div className="flex-1 mx-2">
            <textarea
              ref={textareaRef}
              className="input min-h-[80px] resize-none"
              placeholder="Type your message here... Use / for repository commands"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={disabled}
            />
            {message.startsWith('/analyze-repo') && (
              <div className="mt-1 text-xs text-gray-500 italic">
                Analyzing repository will send its content to the AI for analysis
              </div>
            )}
            {message.startsWith('/analyze-file') && (
              <div className="mt-1 text-xs text-gray-500 italic">
                Analyzing file will send its content to the AI for analysis
              </div>
            )}
          </div>
          
          <Button
            type="submit"
            variant="primary"
            disabled={!message.trim() || disabled}
          >
            Send
          </Button>
        </div>
      </div>
    </form>
  );
};

export default MessageInput;